import React from "react";

import { not } from "@/lib/not";
import { useStableId } from "@/lib/useStableId";
import { EventMap, EventT } from "@/types/event-types";

const eventListener = new Map();
const eventCallbackCache = new Map();

export const useEventSubscriber = <K extends keyof EventMap>(
  event: K,
  callback: (...args: EventMap[K]) => void,
) => {
  // state that tracks if this is the first time the component was created.
  // Enforces rules where events are not duplicated.
  const componentId = useStableId();

  // Cleans up component after it unmounts. So hanging callbacks don't get called for
  // components that no longer exist
  React.useEffect(() => {
    return () => {
      eventListener.get(event).delete(eventCallbackCache.get(componentId));
      eventCallbackCache.delete(componentId);
    };
  }, []);

  // Check if the function has been called previously
  if (not(eventCallbackCache.has(componentId))) {
    if (not(eventListener.has(event))) {
      const callbackSet = new Set();
      callbackSet.add(callback);
      eventListener.set(event, callbackSet);
    } else {
      eventListener.get(event).add(callback);
    }
    eventCallbackCache.set(componentId, callback);
  } else {
    eventListener.get(event).delete(eventCallbackCache.get(componentId));
    eventListener.get(event).add(callback);
    eventCallbackCache.set(componentId, callback);
  }

  return {
    unsubscribe: () => {
      eventListener.get(event).delete(callback);
      eventCallbackCache.delete(componentId);
    },
  };
};

const publisher = (event: string, payload?: unknown) => {
  if (eventListener.get(event) != undefined) {
    eventListener.get(event).forEach((cb: (arg0: unknown) => void) => {
      cb(payload);
    });
  }
};

export const useEventPublisher = () => {
  return publisher;
};

export const emit = <K extends keyof EventMap>(...args: EventT<K>) => {
  publisher(...args);
};
